import { safeSetDoc, reconcileSectionOrder } from '../utils';
import React, { useMemo, useState } from 'react';
import {
  MessageSquare as MessageSquareIcon,
  Calendar as CalendarIcon,
  CheckSquare as CheckSquareIcon,
  FileText as FileTextIcon,
  Plus,
  Pencil,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Lock,
  Unlock,
  Play,
  Pause,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectDataContext';
import { auth, db, useEmulator } from '../firebase';
import { doc, serverTimestamp } from 'firebase/firestore';
import Clock from './Clock';
import WeatherWidget from './WeatherWidget';
import GroceryList from './GroceryList';
import PomodoroTimer from './PomodoroTimer';
import { useRecommendations, refreshRecommendations } from '../hooks/useRecommendations';
import { getActiveLocationsForDate, checkPlacesRateLimit } from '../utils';
import { Search } from 'lucide-react';

const getDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 9999;
  const R = 3958.8;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const WantToGoSearchBox = ({ user, activeLocs, appId }) => {
  const [inputValue, setInputValue] = useState('');
  const [predictions, setPredictions] = useState([]);
  const placesLibraryRef = React.useRef(null);
  const sessionTokenRef = React.useRef(null);
  const searchTimeoutRef = React.useRef(null);

  const refreshSessionToken = () => {
    if (placesLibraryRef.current?.AutocompleteSessionToken) {
      sessionTokenRef.current = new placesLibraryRef.current.AutocompleteSessionToken();
      if (import.meta.env.DEV) console.log('[WantToGoSearchBox] New session token generated');
    }
  };

  React.useEffect(() => {
    const initServices = async () => {
      if (window.google?.maps) {
        try {
          if (!window.google.maps.places) {
            placesLibraryRef.current = await window.google.maps.importLibrary('places');
          } else {
            placesLibraryRef.current = window.google.maps.places;
          }
          refreshSessionToken();
        } catch (e) {
          console.error('[WantToGoSearchBox] Failed to load Google Places:', e);
        }
      }
    };
    initServices();
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (value.trim().length < 5) {
      setPredictions([]);
      if (!value.trim()) {
        refreshSessionToken();
      }
      return;
    }
    
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (!placesLibraryRef.current?.AutocompleteSuggestion) return;
      if (!sessionTokenRef.current) refreshSessionToken();

      const request = {
        input: value,
        includedPrimaryTypes: ['restaurant', 'bar', 'cafe', 'tourist_attraction', 'museum', 'park', 'bakery', 'store'],
        sessionToken: sessionTokenRef.current,
      };
      if (activeLocs && activeLocs.length > 0) {
        const primaryLoc = activeLocs[0];
        if (primaryLoc && primaryLoc.lat && primaryLoc.lng) {
          request.locationRestriction = {
            center: { lat: primaryLoc.lat, lng: primaryLoc.lng },
            radius: 50000,
          };
        }
      }
      const { AutocompleteSuggestion } = placesLibraryRef.current;
      
      if (!checkPlacesRateLimit()) {
        console.warn('[MobileHome] Rate limit hit.');
        return;
      }

      AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
        .then((res) => {
          setPredictions(res.suggestions || []);
        })
        .catch(e => console.error(e));
    }, 1000);
  };

  const handlePredictionSelect = async (suggestion) => {
    if (!suggestion.placePrediction) return;
    const { Place } = placesLibraryRef.current;
    if (!Place) return;

    try {
      const place = new Place({ id: suggestion.placePrediction.placeId });
      
      if (!checkPlacesRateLimit()) return;

      await place.fetchFields({ 
        fields: ['id', 'displayName', 'formattedAddress', 'location', 'regularOpeningHours'],
        sessionToken: sessionTokenRef.current 
      });
      
      const uid = user?.uid || auth.currentUser?.uid;
      if (!uid) return;

      const docId = `${uid}_${place.id}`;
      const recRef = doc(db, 'artifacts', appId, 'public', 'data', 'recommendations', docId);

      await safeSetDoc(recRef, {
        userId: uid,
        placeId: place.id,
        placeName: place.displayName,
        address: place.formattedAddress || null,
        lat: place.location?.lat() || 0,
        lng: place.location?.lng() || 0,
        type: 'restaurant',
        tags: ['Want to Go'],
        note: '',
        weekdayDescriptions: place.regularOpeningHours?.weekdayDescriptions || [],
        createdAt: serverTimestamp(),
      }, { merge: true });

      await safeSetDoc(doc(db, 'artifacts', appId, 'public', 'data', 'places', place.id), {
        placeId: place.id,
        name: place.displayName,
        address: place.formattedAddress || null,
        lat: place.location?.lat() || 0,
        lng: place.location?.lng() || 0,
        hours: place.regularOpeningHours?.weekdayDescriptions || [],
        updatedAt: serverTimestamp(),
        updatedBy: uid
      }, { merge: true }).catch(console.error);

      await refreshRecommendations();
      setInputValue('');
      setPredictions([]);
      refreshSessionToken();
    } catch (e) {
      console.error(e);
      refreshSessionToken();
    }
  };

  return (
    <div className="relative w-full mb-3 px-6">
      <div className="flex items-center px-3 py-2 bg-zinc-50 rounded-xl border border-zinc-200">
        <Search size={14} className="text-zinc-400 mr-2" />
        <input 
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Search to add Want to Go..."
          className="bg-transparent w-full outline-none text-[11px] font-medium text-zinc-800 placeholder:text-zinc-400"
        />
      </div>
      {predictions.length > 0 && (
        <div className="absolute top-full left-6 right-6 mt-1 bg-white border border-zinc-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
          {predictions.map(s => {
            const mainText = s.placePrediction.text.mainText || s.placePrediction.text.toString();
            const secText = s.placePrediction.text.secondaryText || '';
            return (
              <div 
                key={s.placePrediction.placeId} 
                className="px-3 py-2 border-b border-zinc-50 last:border-0 active:bg-zinc-100 flex flex-col cursor-pointer"
                onClick={() => handlePredictionSelect(s)}
              >
                <span className="text-xs font-bold text-zinc-800">{mainText}</span>
                {secText && <span className="text-[10px] text-zinc-500 truncate">{secText}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ProjectSubLinks = ({ projectId, navigate, channels, dispatch }) => {
  const channel = (channels || []).find((c) => c.id === projectId);
  const isDM = channel?.type === 'dm';

  return (
    <div className="flex items-center justify-end gap-2 px-4 py-2 bg-zinc-50/50 border-b border-zinc-100 animate-in fade-in slide-in-from-top-2 duration-300">
      <button
        data-testid={`project-chat-link-${projectId}`}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/p/${projectId}/chat` + window.location.search);
        }}
        className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-zinc-100 shadow-sm active:scale-90 transition-all"
        title="Chat"
      >
        <div className="w-6 h-6 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600 border border-blue-500/5">
          <MessageSquareIcon size={16} strokeWidth={2.5} />
        </div>
      </button>
      {!isDM && (
        <>
          <button
            data-testid={`project-tasks-link-${projectId}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/p/${projectId}/tasks` + window.location.search);
            }}
            className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-zinc-100 shadow-sm active:scale-90 transition-all"
            title="Tasks"
          >
            <div className="w-6 h-6 rounded-xl bg-green-500/10 flex items-center justify-center text-green-600 border border-green-500/5">
              <CheckSquareIcon size={16} strokeWidth={2.5} />
            </div>
          </button>
          <button
            data-testid={`project-notes-link-${projectId}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/p/${projectId}/notes` + window.location.search);
            }}
            className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-zinc-100 shadow-sm active:scale-90 transition-all"
            title="Notepad"
          >
            <div className="w-6 h-6 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-600 border border-purple-500/5">
              <FileTextIcon size={16} strokeWidth={2.5} />
            </div>
          </button>
        </>
      )}
    </div>
  );
};

const ProjectItemRecursive = ({
  p,
  navigate,
  MUTED_COLORS,
  expandedIds,
  activeButtonsId,
  toggleExpand,
  onStarToggle,
  channels,
  user,
  dispatch,
  isStarredList = false,
  getProjectPath,
  isTouch,
  isLocked = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: isStarredList ? `starred-${p.id}` : p.id,
    data: { type: 'channel', channel: p, isStarredList },
    disabled: isLocked,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    touchAction: isDragging ? 'none' : 'pan-y',
  };

  const isExpanded = !!(expandedIds || {})[isStarredList ? `starred:${p.id}` : p.id];
  const showButtons = activeButtonsId === (isStarredList ? `starred:${p.id}` : p.id);
  const children = (channels || []).filter((c) => c.parentId === p.id && c.type !== 'dm');
  children.sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col border-b border-zinc-50 bg-white"
      data-project-id={p.id}
    >
      <div className="w-full flex items-center pr-2">
        {!isLocked ? (
          <button
            {...attributes}
            {...listeners}
            className="p-2 pl-4 pr-3 text-zinc-300 hover:text-zinc-500 active:bg-zinc-100 touch-none flex-shrink-0 cursor-grab active:cursor-grabbing"
          >
            <GripVertical size={16} />
          </button>
        ) : (
          <div className="pl-4"></div>
        )}
        <div
          data-testid={`project-item-${p.id}`}
          onClick={(e) => {
            if (isDragging) return;
            e.stopPropagation();
            toggleExpand(p.id, isStarredList);
          }}
          className="flex-1 flex items-center gap-3 py-2 pl-1 pr-2 active:bg-zinc-50 text-left min-w-0 cursor-pointer"
        >
          <div
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{
              backgroundColor: MUTED_COLORS.find((c) => c.id === p.color)?.hex || '#e4e4e7',
            }}
          />
          <div className="flex flex-col min-w-0 flex-1">
            <span
              data-testid={`project-name-${p.id}`}
              className={`font-bold text-zinc-800 truncate ${p.parentId ? 'text-sm' : 'text-base'}`}
            >
              {p.id === 'general_chat_global' ? 'Tiki Bar' : p.name}
            </span>
            {p.description && (
              <span className="text-[10px] text-zinc-400 font-medium italic truncate">
                {p.description}
              </span>
            )}
          </div>
          <div
            className="ml-auto flex items-center gap-1 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {isExpanded && (
              <>
                <button
                  data-testid={`edit-project-btn-${p.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({ type: 'OPEN_EDIT_CHANNEL', payload: p });
                  }}
                  className="p-1.5 active:bg-zinc-100 rounded text-zinc-300 hover:text-zinc-500"
                >
                  <Pencil size={14} strokeWidth={2.5} />
                </button>
                <button
                  data-testid={`add-subproject-btn-${p.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({ type: 'OPEN_CREATE_CHANNEL', payload: p.id });
                  }}
                  className="p-1.5 active:bg-zinc-100 rounded text-zinc-300 hover:text-zinc-500"
                  title="Add Sub-Project"
                >
                  <Plus size={16} strokeWidth={2.5} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="flex flex-col">
          {showButtons && (
            <ProjectSubLinks
              projectId={p.id}
              navigate={navigate}
              channels={channels}
              dispatch={dispatch}
            />
          )}
          {children.length > 0 && (
            <div className="ml-4 border-l border-zinc-100 flex flex-col mb-2">
              <SortableContext
                items={children.map((c) => (isStarredList ? `starred-${c.id}` : c.id))}
                strategy={verticalListSortingStrategy}
              >
                {children.map((child) => (
                  <ProjectItemRecursive
                    key={child.id}
                    p={child}
                    navigate={navigate}
                    MUTED_COLORS={MUTED_COLORS}
                    expandedIds={expandedIds}
                    activeButtonsId={activeButtonsId}
                    toggleExpand={toggleExpand}
                    onStarToggle={onStarToggle}
                    channels={channels}
                    user={user}
                    dispatch={dispatch}
                    isStarredList={isStarredList}
                    getProjectPath={getProjectPath}
                    isTouch={isTouch}
                    isLocked={isLocked}
                  />
                ))}
              </SortableContext>
            </div>
          )}
        </div>
      )}
    </div>
  );
};



const RADIO_STATIONS = [
  { id: 'worldwide-fm', name: 'Worldwide.fm', streamUrl: 'https://worldwide-fm.radiocult.fm/stream' }
];

const SortableMobileSection = ({ id, isLocked, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { type: 'mobile-section' },
    disabled: isLocked,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group border-t border-transparent">
      {!isLocked && (
        <div 
          {...attributes} 
          {...listeners} 
          className="absolute left-1 top-1 w-6 h-6 flex items-center justify-center text-zinc-300 touch-none cursor-grab active:cursor-grabbing hover:bg-zinc-50 rounded z-10"
        >
          <GripVertical size={16} strokeWidth={2.5} />
        </div>
      )}
      {children}
    </div>
  );
};

const getDistanceColor = (dist) => {
  if (dist < 2) return 'text-green-600 bg-green-50 border-green-100';
  if (dist < 10) return 'text-blue-500 bg-blue-50 border-blue-100';
  if (dist < 25) return 'text-orange-500 bg-orange-50 border-orange-100';
  return 'text-red-500 bg-red-50 border-red-100';
};

const getPlaceStatus = (weekdayDescriptions, activeLocs) => {
  if (!weekdayDescriptions || !weekdayDescriptions.length) return { hours: null, isOpen: false };
  const d = new Date();
  
  const tz = activeLocs?.[0]?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dStr = d.toLocaleString('en-US', { timeZone: tz, weekday: 'long' });
  const dayName = dStr.split(',')[0];
  
  const todayDesc = weekdayDescriptions.find(desc => desc.startsWith(dayName));
  if (!todayDesc) return { hours: null, isOpen: false };
  
  const parts = todayDesc.split(': ');
  if (parts.length < 2) return { hours: null, isOpen: false };
  const hoursStr = parts.slice(1).join(': ').trim();
  
  if (hoursStr.toLowerCase() === 'closed') return { hours: 'Closed today', isOpen: false };
  if (hoursStr.toLowerCase() === 'open 24 hours') return { hours: 'Open 24 hours', isOpen: true };
  
  let isOpen = false;
  try {
    const parseTime = (t) => {
      const match = t.match(/(\d+):(\d+)\s*(AM|PM|am|pm)/);
      if (!match) return 0;
      let h = parseInt(match[1]);
      const m = parseInt(match[2]);
      const ampm = match[3].toUpperCase();
      if (ampm === 'PM' && h !== 12) h += 12;
      if (ampm === 'AM' && h === 12) h = 0;
      return h * 60 + m;
    };
    
    const ranges = hoursStr.split(', ');
    const timeStrFormat = d.toLocaleString('en-US', { timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false });
    const [nowH, nowM] = timeStrFormat.split(':').map(Number);
    const nowMin = nowH * 60 + nowM;
    
    for (const range of ranges) {
      const [startStr, endStr] = range.split(/[–-]/);
      if (startStr && endStr) {
        const startMin = parseTime(startStr);
        const endMin = parseTime(endStr);
        if (endMin < startMin) {
          if (nowMin >= startMin || nowMin <= endMin) isOpen = true;
        } else {
          if (nowMin >= startMin && nowMin <= endMin) isOpen = true;
        }
      }
    }
  } catch (e) {}
  
  return { hours: hoursStr, isOpen };
};

const MobileHome = (props) => {
  const [effectiveSectionOrder, setEffectiveSectionOrder] = useState(() => {
    let savedParsed = null;
    try {
      const saved = localStorage.getItem('mobileSectionOrder');
      if (saved) savedParsed = JSON.parse(saved);
    } catch (e) {}
    return reconcileSectionOrder(savedParsed);
  });

  React.useEffect(() => {
    const handleOrderChange = () => {
      let savedParsed = null;
      try {
        const saved = localStorage.getItem('mobileSectionOrder');
        if (saved) savedParsed = JSON.parse(saved);
      } catch (e) {}
      setEffectiveSectionOrder(reconcileSectionOrder(savedParsed));
    };
    window.addEventListener('mobileSectionOrderChanged', handleOrderChange);
    return () => window.removeEventListener('mobileSectionOrderChanged', handleOrderChange);
  }, []);
  const { user: authUser, profile: authProfile } = useAuth();
  const { channels, allUsers, events, habits, habitLogs, allTasks } = useProject();
  const {
    state,
    dispatch,
    handleGoTikiBar,
    toggleProjectStar,
    getProjectPath,
    isTouch,
    isDesktopDashboard,
    userAvatar: propAvatar,
    motivationalMessage: propMoto,
    user: propUser,
    navigate,
  } = props;
  const { expandedIds, activeButtonsId, appId } = state || {};

  const user = propUser || authUser;
  const uid = user?.uid || user?.id || 'test-uid-123';
  const profile = (allUsers || []).find((u) => u.id === uid) || authProfile;
  const motivationalMessage = propMoto || profile?.motivationalMessage || '';
  const userAvatar = propAvatar || profile?.avatar || '👤';
  const { recommendations } = useRecommendations();

  const [isMobileLocked, setIsMobileLocked] = useState(true);
  const [eventsDaysToShow, setEventsDaysToShow] = useState(1);

  const [isProjectsExpanded, setIsProjectsExpanded] = useState(() => {
    return localStorage.getItem('isMobileProjectsExpanded') !== 'false';
  });
  const toggleProjectsList = () => {
    const newVal = !isProjectsExpanded;
    setIsProjectsExpanded(newVal);
    localStorage.setItem('isMobileProjectsExpanded', newVal);
  };

  const [isBookmarksExpanded, setIsBookmarksExpanded] = useState(() => {
    return localStorage.getItem('isMobileBookmarksExpanded') !== 'false';
  });
  const toggleBookmarksList = () => {
    const newVal = !isBookmarksExpanded;
    setIsBookmarksExpanded(newVal);
    localStorage.setItem('isMobileBookmarksExpanded', newVal);
  };

  const [isWantToGoExpanded, setIsWantToGoExpanded] = useState(() => {
    return localStorage.getItem('isMobileWantToGoExpanded') !== 'false';
  });
  const toggleWantToGoList = () => {
    const newVal = !isWantToGoExpanded;
    setIsWantToGoExpanded(newVal);
    localStorage.setItem('isMobileWantToGoExpanded', newVal);
  };

  const [isToWatchExpanded, setIsToWatchExpanded] = useState(() => {
    return localStorage.getItem('isMobileToWatchExpanded') !== 'false';
  });
  const toggleToWatchList = () => {
    const newVal = !isToWatchExpanded;
    setIsToWatchExpanded(newVal);
    localStorage.setItem('isMobileToWatchExpanded', newVal);
  };

  const [isHabitsExpanded, setIsHabitsExpanded] = useState(() => {
    return localStorage.getItem('isMobileHabitsExpanded') !== 'false';
  });
  const toggleHabitsList = () => {
    const newVal = !isHabitsExpanded;
    setIsHabitsExpanded(newVal);
    localStorage.setItem('isMobileHabitsExpanded', newVal);
  };




  const [isGroceryListExpanded, setIsGroceryListExpanded] = useState(() => {
    return localStorage.getItem('isMobileGroceryListExpanded') !== 'false';
  });
  const toggleGroceryList = () => {
    const newVal = !isGroceryListExpanded;
    setIsGroceryListExpanded(newVal);
    localStorage.setItem('isMobileGroceryListExpanded', newVal);
  };

  const [isPomodoroExpanded, setIsPomodoroExpanded] = useState(() => {
    return localStorage.getItem('isMobilePomodoroExpanded') !== 'false';
  });
  const togglePomodoro = () => {
    const newVal = !isPomodoroExpanded;
    setIsPomodoroExpanded(newVal);
    localStorage.setItem('isMobilePomodoroExpanded', newVal);
  };

  const [isInternetRadioExpanded, setIsInternetRadioExpanded] = useState(() => {
    return localStorage.getItem('isMobileInternetRadioExpanded') !== 'false';
  });
  const toggleInternetRadioList = () => {
    const newVal = !isInternetRadioExpanded;
    setIsInternetRadioExpanded(newVal);
    localStorage.setItem('isMobileInternetRadioExpanded', newVal);
  };

  const { radioStation, isPlayingRadio } = state || {};

  const handlePlayStation = (station) => {
    if (radioStation?.id === station.id) {
      dispatch({ type: 'SET_IS_PLAYING_RADIO', payload: !isPlayingRadio });
    } else {
      dispatch({ type: 'SET_RADIO_STATION', payload: station });
      dispatch({ type: 'SET_IS_PLAYING_RADIO', payload: true });
    }
  };

  const [showYesterdayHabits, setShowYesterdayHabits] = useState(false);
  const [showAnchorAddress, setShowAnchorAddress] = useState(false);

  const activeLocs = useMemo(() => {
    const dateObj = new Date();
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    const trips = (events || []).filter(e => e.type === 'trip');
    const allEvents = events || [];
    const homeCity = profile?.cities?.[0] || {
      name: 'Home',
      lat: 38.9072,
      lng: -77.0369,
      timezone: 'America/New_York'
    };
    return getActiveLocationsForDate(dateStr, dateObj, trips, allEvents, homeCity);
  }, [events, profile]);

  const weatherLocs = useMemo(() => {
    const locs = [];
    
    // 1. Home City
    const homeCity = profile?.cities?.[0] || {
      name: 'Home',
      lat: 38.9072,
      lng: -77.0369,
      timezone: 'America/New_York'
    };
    locs.push({ ...homeCity, label: 'Home' });

    // 2. Today's Travel (from activeLocs, excluding homeCity)
    activeLocs.forEach(loc => {
      if (loc.name !== homeCity.name && loc.city !== homeCity.name) {
         if (!locs.find(l => l.city === loc.city || l.name === loc.name)) {
           locs.push({ ...loc, label: 'Today' });
         }
      }
    });

    // 3. Next 7 Days Trips
    const todayObj = new Date();
    for (let i = 1; i <= 7; i++) {
      const d = new Date(todayObj);
      d.setDate(d.getDate() + i);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dayStr = String(d.getDate()).padStart(2, '0');
      const ds = `${y}-${m}-${dayStr}`;

      const dTrips = (events || []).filter(e => e.type === 'trip' && e.startDate <= ds && e.endDate >= ds);
      dTrips.forEach(t => {
        if (!locs.find(l => l.city === t.city || l.name === t.city)) {
          locs.push({ name: t.city, city: t.city, lat: t.lat, lng: t.lng, timezone: t.timezoneSource, label: 'Upcoming' });
        }
      });
    }

    return locs;
  }, [profile, activeLocs, events]);

  const [isWeatherExpanded, setIsWeatherExpanded] = useState(() => {
    return localStorage.getItem('isMobileWeatherExpanded') !== 'false';
  });
  const toggleWeatherList = () => {
    const newVal = !isWeatherExpanded;
    setIsWeatherExpanded(newVal);
    localStorage.setItem('isMobileWeatherExpanded', newVal);
  };

  const [wantToGoSort, setWantToGoSort] = useState('newest');

  const wantToGoPlaces = useMemo(() => {
    if (!recommendations) return [];
    const myRecs = recommendations.filter(r => r.userId === user?.uid && r.tags?.some(t => typeof t === 'string' && t.toLowerCase() === 'want to go'));
    const mapped = myRecs.map(rec => {
      let minDistance = Infinity;
      for (const loc of activeLocs) {
        const d = getDistance(loc.lat, loc.lng, rec.lat, rec.lng);
        if (d < minDistance) minDistance = d;
      }
      return { ...rec, minDistance };
    });
    const cityItems = mapped.filter(item => item.minDistance <= 50);
    cityItems.sort((a, b) => {
       if (wantToGoSort === 'closest') {
         return (a.minDistance || 0) - (b.minDistance || 0);
       }
       const tA = a.createdAt?.seconds || 0;
       const tB = b.createdAt?.seconds || 0;
       return wantToGoSort === 'newest' ? tB - tA : tA - tB;
    });
    return cityItems;
  }, [recommendations, activeLocs, user, wantToGoSort]);

  const formatOptions = { month: 'short', day: 'numeric' };
  const todayObj = new Date();
  const todayFormatted = todayObj.toLocaleDateString('en-US', formatOptions);
  const yesterdayObj = new Date();
  yesterdayObj.setDate(yesterdayObj.getDate() - 1);
  const yesterdayFormatted = yesterdayObj.toLocaleDateString('en-US', formatOptions);

  const habitTargetDate = useMemo(() => {
    const d = new Date();
    
    if (showYesterdayHabits) {
      d.setDate(d.getDate() - 1);
    }
    return d;
  }, [showYesterdayHabits]);

  const hY = habitTargetDate.getFullYear();
  const hM = String(habitTargetDate.getMonth() + 1).padStart(2, '0');
  const hD = String(habitTargetDate.getDate()).padStart(2, '0');
  const habitDateStr = `${hY}-${hM}-${hD}`;
  const habitMonthKey = habitDateStr.substring(0, 7);
  
  const activeHabits = useMemo(() => {
    return (habits || []).filter((habit) => {
      const start = habit.startDate || '0000-00-00';
      const end = habit.endDate || '9999-12-31';
      return habitDateStr >= start && habitDateStr <= end;
    }).sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [habits, habitDateStr]);

  const habitDocId = `${uid}_${habitMonthKey}`;
  const habitLogDoc = (habitLogs || []).find((l) => l.id === habitDocId);
  const targetDayLog = habitLogDoc?.days?.[habitDateStr] || {};

  const handleHabitToggle = (habit) => {
    const entry = targetDayLog[habit.id] || {};
    const completed = !!entry.completed;

    props.handleLogHabit?.(habitMonthKey, habitDateStr, habit.id, { ...entry, completed: !completed }, showYesterdayHabits);
  };

  const todayStr = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dayStr = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dayStr}`;
  }, []);

  const eventsByDay = useMemo(() => {
    const arr = [];
    for (let i = 0; i < eventsDaysToShow; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dayStr = String(d.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${dayStr}`;
      
      const dayLabel = i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
      
      const dayEvents = (events || []).filter(e => {
        if (e.type === 'trip') {
          return e.startDate <= dateStr && e.endDate >= dateStr;
        }
        return e.startDate === dateStr || (e.start && e.start.startsWith(dateStr));
      }).sort((a, b) => {
        if (a.type === 'trip') return -1;
        if (b.type === 'trip') return 1;
        return (a.start || '').localeCompare(b.start || '');
      });
      
      arr.push({ dateStr, dayLabel, events: dayEvents });
    }
    return arr;
  }, [events, eventsDaysToShow]);

  const [isEventsExpanded, setIsEventsExpanded] = useState(() => {
    return localStorage.getItem('isMobileEventsExpanded') !== 'false';
  });
  const toggleEventsList = () => {
    const newVal = !isEventsExpanded;
    setIsEventsExpanded(newVal);
    localStorage.setItem('isMobileEventsExpanded', newVal);
  };

  const personalTasks = useMemo(() => {
    return (allTasks || [])
      .filter(t => t.channelId === `personal-${uid}`)
      .filter(t => !t.completed || t.completedAt?.startsWith?.(todayStr))
      .sort((a, b) => {
        if (a.completed && !b.completed) return 1;
        if (!a.completed && b.completed) return -1;
        return (a.order || 0) - (b.order || 0);
      });
  }, [allTasks, uid, todayStr]);

  const [taskFilters, setTaskFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('mobileTaskFilters');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return { high: true, medium: true, low: true };
  });

  const toggleTaskFilter = (priority) => {
    setTaskFilters(prev => {
      const next = { ...prev, [priority]: !prev[priority] };
      localStorage.setItem('mobileTaskFilters', JSON.stringify(next));
      return next;
    });
  };

  const filteredTasks = useMemo(() => {
    return personalTasks.filter(t => {
      if (t.isFolder) return false; // don't show folders in this view
      const p = (t.header || '').toLowerCase();
      if (p === 'high' && !taskFilters.high) return false;
      if (p === 'medium' && !taskFilters.medium) return false;
      if (p === 'low' && !taskFilters.low) return false;
      return true;
    });
  }, [personalTasks, taskFilters]);

  const [isTasksExpanded, setIsTasksExpanded] = useState(() => {
    return localStorage.getItem('isMobileTasksExpanded') !== 'false';
  });
  const toggleTasksList = () => {
    const newVal = !isTasksExpanded;
    setIsTasksExpanded(newVal);
    localStorage.setItem('isMobileTasksExpanded', newVal);
  };

  const location = useLocation();
  // Global ref to debounce toggleExpand to prevent double clicks in mobile emulation
  const toggleTimeoutRef = React.useRef(null);

  const toggleExpand = (id, isStarred = false) => {
    if (state.activeId) return;
    if (toggleTimeoutRef.current) return;

    toggleTimeoutRef.current = setTimeout(() => {
      toggleTimeoutRef.current = null;
    }, 100);

    const stateId = isStarred ? `starred:${id}` : id;
    const path = getProjectPath(id, channels || []).map((c) => {
      return isStarred ? `starred:${c.id}` : c.id;
    });
    dispatch({ type: 'SET_ACTIVE_BUTTONS', payload: { id: stateId, pathIds: path } });
  };

  const rootProjects = useMemo(() => {
    return (channels || []).filter(
      (c) =>
        !c.parentId &&
        c.type !== 'dm' &&
        c.id !== 'general_chat_global' &&
        (c.participants || [c.createdBy]).includes(user?.uid)
    );
  }, [channels, user]);

  const isWorkingChannel = window.location.hostname.includes('--working-');
  const envEmoji = useEmulator ? '🌵' : isWorkingChannel ? '🍁' : '🌴';

  const homeCityName = profile?.cities?.[0]?.name || 'Home';
  const activeLoc = activeLocs?.find(l => l.name !== homeCityName && l.city !== homeCityName) || activeLocs?.[0];
  const currentLocName = activeLoc?.name || homeCityName;
  const currentLocAddress = activeLoc?.address || null;

  const handleGPSClick = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            if (status === "OK" && results[0]) {
              const addressComponents = results[0].address_components;
              let city = "Current Location";
              const locality = addressComponents.find(c => c.types.includes("locality"));
              const neighborhood = addressComponents.find(c => c.types.includes("neighborhood"));
              if (locality) city = locality.short_name;
              else if (neighborhood) city = neighborhood.short_name;

              dispatch({
                type: 'SET_MAP_FILTER_STATE',
                payload: {
                  customCenter: {
                    lat: latitude,
                    lng: longitude,
                    name: city,
                    address: results[0].formatted_address
                  }
                }
              });
            } else {
              dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { customCenter: { lat: latitude, lng: longitude, name: 'Current Location' } } });
            }
          });
        } else {
          dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { customCenter: { lat: latitude, lng: longitude, name: 'Current Location' } } });
        }
      },
      (error) => {
        alert("Unable to retrieve your location. Please check your browser permissions.");
      }
    );
  };

  return (
    <div className={`flex flex-col bg-white h-full overflow-y-auto font-sans ${isDesktopDashboard ? 'pb-4' : 'pb-10'}`}>
      <header className={`px-6 pt-6 pb-4 flex items-center justify-between relative ${isDesktopDashboard ? 'bg-white z-10 sticky top-0 border-b border-zinc-200 shadow-sm' : ''}`}>
        <div className="flex-shrink-0 flex items-start gap-2 relative z-50">
          <div className="flex flex-col">
            <span className="text-[10px] font-black tracking-[0.3em] text-blue-500 pointer-events-none select-none leading-tight">
              NYTM
            </span>
            <span 
              className="text-[7px] font-bold text-zinc-400 uppercase tracking-[0.1em] mt-0.5 line-clamp-2 whitespace-normal leading-[1.2] w-[45px]"
              title={currentLocAddress || currentLocName}
            >
              {currentLocAddress || currentLocName}
            </span>
          </div>
          <button
            onClick={handleGPSClick}
            className="p-1 mt-0.5 rounded text-blue-500 hover:bg-blue-50 transition-colors"
            title="Set GPS Location"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        </div>
        
        <div className="flex-1 flex justify-center items-center gap-1">
          {(props.headerClocks || []).map((clock, idx) => (
            <Clock
              key={idx}
              timezone={clock.timezone}
              city={clock.city}
              lat={clock.lat}
              lng={clock.lng}
            />
          ))}
        </div>

        <div className="flex-shrink-0 relative top-1 flex items-center gap-4">
          <button
            onClick={() => setIsMobileLocked(!isMobileLocked)}
            className={`transition-all ${isMobileLocked ? 'text-zinc-300 hover:text-zinc-400' : 'text-blue-500'}`}
            title={isMobileLocked ? "Unlock Sections" : "Lock Sections"}
          >
            {isMobileLocked ? <Lock size={20} strokeWidth={2.5} /> : <Unlock size={20} strokeWidth={2.5} />}
          </button>
          <button
            onClick={() => navigate('/settings')}
            className="active:scale-90 transition-transform block text-3xl hover:opacity-80"
          >
            {envEmoji}
          </button>
        </div>
      </header>

      <section className={`px-6 mb-3 grid grid-cols-3 gap-2 ${isDesktopDashboard ? 'pt-6' : ''}`}>
        <button
          onClick={() => navigate(`/p/personal-${user?.uid}/tasks`)}
          className="flex flex-col items-center justify-center py-2 bg-green-500 rounded-2xl border border-green-600 active:bg-green-600 transition-all shadow-md"
        >
          <CheckSquareIcon size={18} className="text-white mb-1" strokeWidth={3} />
          <span className="font-black text-[9px] text-white uppercase tracking-tight">
            My Tasks
          </span>
        </button>
        <button
          onClick={() => navigate(`/p/personal-note-${user?.uid}/notes`)}
          className="flex flex-col items-center justify-center py-2 bg-purple-500 rounded-2xl border border-purple-600 active:bg-purple-600 transition-all shadow-md"
        >
          <FileTextIcon size={18} className="text-white mb-1" strokeWidth={3} />
          <span className="font-black text-[9px] text-white uppercase tracking-tight">
            My Notes
          </span>
        </button>
        <button
          onClick={() => navigate('/calendar')}
          className="flex flex-col items-center justify-center py-2 bg-blue-500 rounded-2xl border border-blue-600 active:bg-blue-600 transition-all shadow-md"
        >
          <CalendarIcon size={18} className="text-white mb-1" strokeWidth={3} />
          <span className="font-black text-[9px] text-white uppercase tracking-tight">
            Calendar
          </span>
        </button>
      </section>

      <section className="px-6 mb-4 grid grid-cols-3 gap-2">
        <button
          onClick={() => dispatch({ type: 'OPEN_ADD_EVENT', payload: { type: 'task' } })}
          className="flex flex-col items-center justify-center py-1.5 bg-zinc-50 rounded-2xl border border-zinc-200 active:bg-zinc-100 transition-all shadow-sm"
        >
          <Plus size={16} className="text-zinc-600 mb-0.5" strokeWidth={3.5} />
          <span className="font-black text-[9px] text-zinc-600 uppercase tracking-tight">
            Task
          </span>
        </button>
        <button
          onClick={() => dispatch({ type: 'OPEN_ADD_EVENT', payload: { type: 'tentative' } })}
          className="flex flex-col items-center justify-center py-1.5 bg-zinc-50 rounded-2xl border border-zinc-200 active:bg-zinc-100 transition-all shadow-sm"
        >
          <Plus size={16} className="text-zinc-600 mb-0.5" strokeWidth={3.5} />
          <span className="font-black text-[9px] text-zinc-600 uppercase tracking-tight">
            Tentative
          </span>
        </button>
        <button
          onClick={() => dispatch({ type: 'OPEN_ADD_EVENT', payload: { type: 'event' } })}
          className="flex flex-col items-center justify-center py-1.5 bg-zinc-50 rounded-2xl border border-zinc-200 active:bg-zinc-100 transition-all shadow-sm"
        >
          <Plus size={16} className="text-zinc-600 mb-0.5" strokeWidth={3.5} />
          <span className="font-black text-[9px] text-zinc-600 uppercase tracking-tight">
            Event
          </span>
        </button>
      </section>

      <SortableContext
        items={effectiveSectionOrder}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col w-full pb-8 gap-1">
          {effectiveSectionOrder.map(id => {
            let content = null;
            if (id === 'projects') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <button onClick={toggleProjectsList} className="flex items-center gap-1 active:opacity-70 transition-opacity">
                      <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                        Projects
                      </h2>
                      {isProjectsExpanded ? (
                        <ChevronDown size={14} strokeWidth={3} className="text-zinc-400" />
                      ) : (
                        <ChevronRight size={14} strokeWidth={3} className="text-zinc-400" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/home');
                        dispatch({ type: 'OPEN_CREATE_CHANNEL', payload: null });
                      }}
                      className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600"
                    >
                      <Plus size={18} strokeWidth={2.5} />
                    </button>
                  </div>

                  {isProjectsExpanded && (
                    <div className="flex flex-col">
                      <SortableContext
                        items={rootProjects.map((p) => p.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {rootProjects.map((p) => (
                          <ProjectItemRecursive
                            key={p.id}
                            p={p}
                            navigate={navigate}
                            MUTED_COLORS={MUTED_COLORS}
                            expandedIds={expandedIds}
                            activeButtonsId={activeButtonsId}
                            toggleExpand={toggleExpand}
                            onStarToggle={toggleProjectStar}
                            channels={channels}
                            user={user}
                            dispatch={dispatch}
                            getProjectPath={getProjectPath}
                            isTouch={isTouch}
                            isLocked={isMobileLocked}
                          />
                        ))}
                      </SortableContext>
                    </div>
                  )}
                </section>
              );
            } else if (id === 'bookmarks') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <div className="flex items-center gap-1">
                      <button className="active:opacity-70 transition-opacity">
                        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                          Bookmarks
                        </h2>
                      </button>
                      <button onClick={toggleBookmarksList} className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600">
                        {isBookmarksExpanded ? (
                          <ChevronDown size={14} strokeWidth={3} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={3} />
                        )}
                      </button>
                    </div>
                    <button
                      onClick={() => {}}
                      className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600"
                    >
                      <Plus size={18} strokeWidth={2.5} />
                    </button>
                  </div>
                  {isBookmarksExpanded && (
                    <div className="flex flex-col">
                      {/* Empty for now */}
                    </div>
                  )}
                </section>
              );
            } else if (id === 'wantToGo') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <div className="flex items-center gap-1">
                      <button className="active:opacity-70 transition-opacity">
                        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                          Want To Go
                        </h2>
                      </button>
                      <button onClick={toggleWantToGoList} className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600">
                        {isWantToGoExpanded ? (
                          <ChevronDown size={14} strokeWidth={3} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={3} />
                        )}
                      </button>
                    </div>
                    <button
                      onClick={() => {}}
                      className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600"
                    >
                      <Plus size={18} strokeWidth={2.5} />
                    </button>
                  </div>
                  {isWantToGoExpanded && (
                    <div className="flex flex-col pb-4">
                      <WantToGoSearchBox user={user} activeLocs={activeLocs} appId={appId} />
                      {wantToGoPlaces.length > 0 && (
                        <div className="flex items-center justify-between px-6 py-2 bg-zinc-50 border-b border-zinc-100">
                          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Sort By:</span>
                          <select
                            value={wantToGoSort}
                            onChange={(e) => setWantToGoSort(e.target.value)}
                            className="text-xs font-bold text-blue-600 bg-transparent outline-none cursor-pointer"
                          >
                            <option value="newest">Newest</option>
                            <option value="oldest">Oldest</option>
                            <option value="closest">Closest</option>
                          </select>
                        </div>
                      )}
                      {wantToGoPlaces.map(place => {
                        const dateStr = place.createdAt?.seconds 
                          ? new Date(place.createdAt.seconds * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) 
                          : 'Unknown date';
                        const distStr = place.minDistance !== Infinity ? `${Math.round(place.minDistance)} mi` : '';
                        const distColorClass = place.minDistance !== Infinity ? getDistanceColor(place.minDistance) : '';
                        const placeStatus = getPlaceStatus(place.weekdayDescriptions, activeLocs);

                        return (
                          <div 
                            key={place.id} 
                            className="flex flex-col py-3 px-6 border-b border-zinc-50 last:border-0 cursor-pointer active:bg-zinc-50"
                            onClick={() => window.open(`https://maps.google.com/?q=${place.lat},${place.lng}`)}
                          >
                            <div className="flex justify-between items-start mb-0.5">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-bold text-zinc-800 text-[13px]">{place.placeName}</span>
                                {placeStatus.hours && (
                                  <div className="flex items-center gap-1">
                                    <div className={`w-1.5 h-1.5 rounded-full ${placeStatus.isOpen ? 'bg-green-500' : 'bg-red-500'}`} />
                                    <span className="text-[9px] font-bold text-zinc-400 truncate max-w-[100px]">{placeStatus.hours}</span>
                                  </div>
                                )}
                              </div>
                              {distStr && <span className={`text-[10px] font-bold border px-1.5 py-0.5 rounded whitespace-nowrap ml-2 ${distColorClass}`}>{distStr}</span>}
                            </div>
                            <span className="text-[11px] text-zinc-400 font-medium mb-1">{place.address}</span>
                            <span className="text-[9px] text-zinc-300 font-bold uppercase tracking-wider">Added: {dateStr}</span>
                          </div>
                        );
                      })}
                      {wantToGoPlaces.length === 0 && (
                        <div className="px-6 py-4 text-center text-zinc-400 text-xs italic font-medium">
                          No Want to Go places near you. Add some above!
                        </div>
                      )}
                    </div>
                  )}
                </section>
              );
            } else if (id === 'toWatch') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <div className="flex items-center gap-1">
                      <button className="active:opacity-70 transition-opacity">
                        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                          To Watch
                        </h2>
                      </button>
                      <button onClick={toggleToWatchList} className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600">
                        {isToWatchExpanded ? (
                          <ChevronDown size={14} strokeWidth={3} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={3} />
                        )}
                      </button>
                    </div>
                    <button
                      onClick={() => {}}
                      className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600"
                    >
                      <Plus size={18} strokeWidth={2.5} />
                    </button>
                  </div>
                  {isToWatchExpanded && (
                    <div className="flex flex-col">
                      {/* Empty for now */}
                    </div>
                  )}
                </section>
              );
            } else if (id === 'habits') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <div className="flex items-center gap-1">
                      <button className="active:opacity-70 transition-opacity">
                        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                          Habits
                        </h2>
                      </button>
                      <button onClick={toggleHabitsList} className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600">
                        {isHabitsExpanded ? (
                          <ChevronDown size={14} strokeWidth={3} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={3} />
                        )}
                      </button>
                    </div>
                    <button
                      onClick={() => dispatch({ type: 'OPEN_ADD_EVENT', payload: { type: 'habit' } })}
                      className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600"
                    >
                      <Plus size={18} strokeWidth={2.5} />
                    </button>
                  </div>
                  {isHabitsExpanded && (
                    <div className="flex flex-col px-6">
                      <div className="flex justify-end mb-2">
                        <button
                          onClick={() => setShowYesterdayHabits(!showYesterdayHabits)}
                          className={`text-[10px] font-bold px-2 py-1 rounded border transition-all ${
                            showYesterdayHabits 
                              ? 'bg-blue-100 text-blue-700 border-blue-200' 
                              : 'bg-zinc-50 text-zinc-400 border-zinc-200 hover:bg-zinc-100'
                          }`}
                        >
                          {showYesterdayHabits ? `Show Today (${todayFormatted})` : `Yesterday (${yesterdayFormatted})`}
                        </button>
                      </div>
                      {activeHabits.map(habit => {
                        const entry = targetDayLog[habit.id] || {};
                        const completed = !!entry.completed;
                        return (
                          <div 
                            key={habit.id}
                            className="flex items-center gap-2 py-1.5 border-b border-zinc-50 last:border-0 cursor-pointer active:bg-zinc-50"
                            onClick={() => handleHabitToggle(habit)}
                          >
                            <button 
                              className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                                completed 
                                  ? 'bg-blue-500 border-blue-500 text-white' 
                                  : 'bg-white border-zinc-300 text-transparent'
                              }`}
                            >
                              <CheckSquareIcon size={12} strokeWidth={4} />
                            </button>
                            <span className={`text-[13px] font-bold flex-1 truncate ${completed ? 'text-zinc-400 line-through' : 'text-zinc-800'}`}>
                              {habit.title}
                            </span>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                dispatch({ type: 'OPEN_EDIT_EVENT', payload: habit });
                              }}
                              className="p-1.5 active:bg-zinc-100 rounded text-zinc-300 hover:text-zinc-500"
                            >
                              <Pencil size={14} strokeWidth={2.5} />
                            </button>
                          </div>
                        );
                      })}
                      {activeHabits.length === 0 && (
                        <div className="py-4 text-center text-zinc-400 text-xs italic font-medium">
                          No active habits. Add some!
                        </div>
                      )}
                    </div>
                  )}
                </section>
              );
              } else if (id === 'groceries') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <div className="flex items-center gap-1">
                      <button className="active:opacity-70 transition-opacity">
                        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                          Grocery List
                        </h2>
                      </button>
                      <button onClick={toggleGroceryList} className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600">
                        {isGroceryListExpanded ? (
                          <ChevronDown size={14} strokeWidth={3} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={3} />
                        )}
                      </button>
                    </div>
                  </div>
                  {isGroceryListExpanded && (
                    <div className="px-0 pb-4">
                      <GroceryList user={user} appId={appId} />
                    </div>
                  )}
                </section>
              );
            } else if (id === 'pomodoro') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <div className="flex items-center gap-1">
                      <button className="active:opacity-70 transition-opacity">
                        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                          Pomodoro
                        </h2>
                      </button>
                      <button onClick={togglePomodoro} className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600">
                        {isPomodoroExpanded ? (
                          <ChevronDown size={14} strokeWidth={3} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={3} />
                        )}
                      </button>
                    </div>
                  </div>
                  {isPomodoroExpanded && (
                    <div className="px-0 pb-4">
                      <div className="px-6 flex justify-center">
                        <PomodoroTimer />
                      </div>
                    </div>
                  )}
                </section>
              );
            } else if (id === 'internetRadio') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <div className="flex items-center gap-1">
                      <button className="active:opacity-70 transition-opacity">
                        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                          Internet Radio
                        </h2>
                      </button>
                      <button onClick={toggleInternetRadioList} className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600">
                        {isInternetRadioExpanded ? (
                          <ChevronDown size={14} strokeWidth={3} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={3} />
                        )}
                      </button>
                    </div>
                  </div>
                  {isInternetRadioExpanded && (
                    <div className="flex flex-col border-t border-zinc-100 pb-2 pt-2">
                      {RADIO_STATIONS.map(station => {
                        const isActive = radioStation?.id === station.id;
                        return (
                          <div 
                            key={station.id} 
                            className="flex items-center gap-3 py-3 px-6 border-b border-zinc-50 last:border-0 cursor-pointer active:bg-zinc-50"
                            onClick={() => handlePlayStation(station)}
                          >
                            <button className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${isActive && isPlayingRadio ? 'bg-blue-500 text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                              {isActive && isPlayingRadio ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" className="ml-0.5" />}
                            </button>
                            <div className="flex flex-col">
                              <span className={`text-[13px] font-bold ${isActive ? 'text-blue-600' : 'text-zinc-800'}`}>
                                {station.name}
                              </span>
                              {isActive && isPlayingRadio && (
                                <span className="text-[10px] text-blue-500 font-medium animate-pulse">Playing...</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            } else if (id === 'weather') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <div className="flex items-center gap-1">
                      <button className="active:opacity-70 transition-opacity">
                        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                          Weather
                        </h2>
                      </button>
                      <button onClick={toggleWeatherList} className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600">
                        {isWeatherExpanded ? (
                          <ChevronDown size={14} strokeWidth={3} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={3} />
                        )}
                      </button>
                    </div>
                  </div>
                  {isWeatherExpanded && (
                    <div className="flex flex-col border-t border-zinc-100">
                      {weatherLocs.map((loc, idx) => (
                         <div key={idx} className="mb-1 last:mb-0">
                           <div className="px-6 py-1.5 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between">
                              <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">{loc.label}</span>
                              <span className="text-[10px] font-medium text-zinc-500">{loc.name || loc.city}</span>
                           </div>
                           <WeatherWidget 
                             lat={loc.lat} 
                             lon={loc.lng} 
                             locationName={loc.name || loc.city} 
                             height={180} 
                             isFull={false}
                             showDetails={true}
                           />
                         </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            } else if (id === 'todayEvents') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <div className="flex items-center gap-1">
                      <button className="active:opacity-70 transition-opacity">
                        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                          Events
                        </h2>
                      </button>
                      <button onClick={toggleEventsList} className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600">
                        {isEventsExpanded ? (
                          <ChevronDown size={14} strokeWidth={3} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={3} />
                        )}
                      </button>
                    </div>
                  </div>
                  {isEventsExpanded && (
                    <div className="flex flex-col border-t border-zinc-100">
                      {eventsByDay.map((dayObj) => (
                        <div key={dayObj.dateStr} className="flex flex-col">
                          {eventsDaysToShow > 1 && (
                            <div className="px-6 py-1.5 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between">
                              <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">{dayObj.dayLabel}</span>
                            </div>
                          )}
                          {dayObj.events.map(e => {
                            let displayTitle = e.title;
                            if (!displayTitle) {
                              displayTitle = e.venueName || e.address || e.city || 'Untitled Event';
                            }
                            
                            const displayLoc = e.venueName || e.address || e.city || '';
                            const showLoc = displayLoc && displayTitle !== displayLoc;

                            return (
                              <div key={e.id} className="flex flex-col py-3 px-6 border-b border-zinc-50 last:border-0 cursor-pointer active:bg-zinc-50">
                                <span className="font-bold text-zinc-800 text-[13px]">{displayTitle}</span>
                                {showLoc && (
                                  <span className="text-[11px] text-zinc-400 font-medium">{displayLoc}</span>
                                )}
                                {e.type === 'event' && e.start && (
                                  <span className="text-[11px] text-zinc-500 mt-0.5">{new Date(e.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                )}
                                {e.type === 'trip' && (
                                  <span className="text-[11px] text-zinc-500 mt-0.5">{e.city || 'Trip'} (All Day)</span>
                                )}
                              </div>
                            );
                          })}
                          {dayObj.events.length === 0 && (
                            <div className="px-6 py-3 text-center text-zinc-400 text-xs italic font-medium border-b border-zinc-50">
                              No events scheduled.
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="flex border-t border-zinc-50">
                        {eventsDaysToShow < 7 && (
                          <>
                            <button
                              onClick={() => setEventsDaysToShow(prev => Math.min(prev + 1, 7))}
                              className="flex-1 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-400 hover:text-zinc-600 active:bg-zinc-50 transition-colors border-r border-zinc-50"
                            >
                              {eventsDaysToShow === 1 ? 'Tomorrow' : 'Next Day'}
                            </button>
                            <button
                              onClick={() => setEventsDaysToShow(7)}
                              className="flex-1 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-400 hover:text-zinc-600 active:bg-zinc-50 transition-colors"
                            >
                              This Week
                            </button>
                          </>
                        )}
                        {eventsDaysToShow > 1 && (
                          <button
                            onClick={() => setEventsDaysToShow(1)}
                            className="flex-1 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-400 hover:text-zinc-600 active:bg-zinc-50 transition-colors border-l border-zinc-50"
                          >
                            Collapse
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              );
            } else if (id === 'myTasks') {
              content = (
                <section className="w-full pt-2">
                  <div className="flex items-center justify-between px-6 pb-2">
                    <div className="flex items-center gap-1">
                      <button className="active:opacity-70 transition-opacity">
                        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                          My Tasks
                        </h2>
                      </button>
                      <button onClick={toggleTasksList} className="p-1 active:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600">
                        {isTasksExpanded ? (
                          <ChevronDown size={14} strokeWidth={3} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={3} />
                        )}
                      </button>
                    </div>
                    {isTasksExpanded && (
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => toggleTaskFilter('high')} className={`text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors ${taskFilters.high ? 'bg-red-100 text-red-600' : 'bg-zinc-100 text-zinc-400'}`}>High</button>
                        <button onClick={() => toggleTaskFilter('medium')} className={`text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors ${taskFilters.medium ? 'bg-orange-100 text-orange-600' : 'bg-zinc-100 text-zinc-400'}`}>Med</button>
                        <button onClick={() => toggleTaskFilter('low')} className={`text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors ${taskFilters.low ? 'bg-blue-100 text-blue-600' : 'bg-zinc-100 text-zinc-400'}`}>Low</button>
                      </div>
                    )}
                  </div>
                  {isTasksExpanded && (
                    <div className="flex flex-col border-t border-zinc-100">
                      {filteredTasks.map(t => {
                        const p = (t.header || '').toLowerCase();
                        let dotColor = 'bg-zinc-300';
                        if (p === 'high') dotColor = 'bg-red-500';
                        if (p === 'medium') dotColor = 'bg-orange-500';
                        if (p === 'low') dotColor = 'bg-blue-500';

                        return (
                          <div 
                            key={t.id} 
                            className="flex items-center gap-2 py-2 px-6 border-b border-zinc-50 last:border-0 cursor-pointer active:bg-zinc-50 group"
                            onClick={() => navigate(`/p/personal-${uid}/tasks`)}
                          >
                            <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 transition-colors ${t.completed ? 'bg-green-500 border-green-500' : 'bg-white border-zinc-300 group-hover:border-zinc-400'}`}>
                              {t.completed && <CheckSquareIcon size={10} className="text-white" strokeWidth={4} />}
                            </div>
                            <div className="flex items-center flex-1 min-w-0 gap-2">
                              <span className={`text-[11px] font-medium truncate ${t.completed ? 'text-zinc-400 line-through' : 'text-zinc-700'}`}>
                                {t.text}
                              </span>
                              {!t.completed && <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />}
                            </div>
                          </div>
                        );
                      })}
                      {filteredTasks.length === 0 && (
                        <div className="px-6 py-3 text-center text-zinc-400 text-[10px] italic font-medium">
                          No tasks match current filters.
                        </div>
                      )}
                    </div>
                  )}
                </section>
              );
            }

            return content ? <SortableMobileSection key={id} id={id} isLocked={isMobileLocked}>{content}</SortableMobileSection> : null;
          })}
        </div>
      </SortableContext>

    </div>
  );
};

const MUTED_COLORS = [
  { id: 'default', hex: '#ffffff' },
  { id: 'red', hex: '#fecaca' },
  { id: 'orange', hex: '#fed7aa' },
  { id: 'yellow', hex: '#fef08a' },
  { id: 'green', hex: '#bbf7d0' },
  { id: 'blue', hex: '#bfdbfe' },
  { id: 'purple', hex: '#ddd6fe' },
  { id: 'pink', hex: '#fbcfe8' },
];

export default MobileHome;
